export const PROJECT_FOLDERS_STORAGE = "cue-studio-project-folders-v1";
export const MAX_PROJECT_FOLDERS = 100;
export const MAX_PROJECT_NAME = 80;

export interface ProjectFolder {
  id: string;
  name: string;
  scriptIds: string[];
}
export interface ProjectFoldersState {
  version: 1;
  folders: ProjectFolder[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const validId = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0 && value.length < 200;
export function cleanProjectName(value: string): string {
  return value.replace(/[\u0000-\u001f\u007f]/gu, " ").replace(/\s+/gu, " ").trim().slice(0, MAX_PROJECT_NAME);
}
const nameKey = (name: string) => name.normalize("NFKC").toLocaleLowerCase("en");
export function projectNameExists(folders: ProjectFolder[], name: string, exceptId?: string): boolean {
  return folders.some(folder => folder.id !== exceptId && nameKey(folder.name) === nameKey(name));
}
function uniqueRestoredName(name: string, folders: ProjectFolder[]): string {
  if (!projectNameExists(folders, name)) return name;
  for (let index = 1; ; index++) {
    const suffix = index === 1 ? " (restored)" : ` (restored ${index})`;
    const candidate = name.slice(0, MAX_PROJECT_NAME - suffix.length) + suffix;
    if (!projectNameExists(folders, candidate)) return candidate;
  }
}

/** Repair stored membership without ever changing or dropping a script. */
export function normalizeProjectFolders(value: unknown, scriptIds?: Iterable<string>): ProjectFoldersState {
  const folders: ProjectFolder[] = [];
  if (!isRecord(value) || value.version !== 1 || !Array.isArray(value.folders)) return { version: 1, folders };
  const allowed = scriptIds === undefined ? null : new Set(scriptIds);
  const assigned = new Set<string>();
  const ids = new Set<string>();
  for (const item of value.folders.slice(0, MAX_PROJECT_FOLDERS)) {
    if (!isRecord(item) || !validId(item.id) || ids.has(item.id) || typeof item.name !== "string" || !Array.isArray(item.scriptIds)) continue;
    const name = cleanProjectName(item.name);
    if (!name) continue;
    const members: string[] = [];
    for (const id of item.scriptIds.slice(0, 300)) {
      if (!validId(id) || assigned.has(id) || (allowed && !allowed.has(id))) continue;
      members.push(id);
      assigned.add(id);
    }
    ids.add(item.id);
    folders.push({ id: item.id, name: uniqueRestoredName(name, folders), scriptIds: members });
  }
  return { version: 1, folders };
}

/** Backups reject unsupported structures; missing members are safely omitted after remapping. */
export function validProjectFoldersBackup(value: unknown): value is ProjectFoldersState {
  if (!isRecord(value) || value.version !== 1 || !Array.isArray(value.folders) || value.folders.length > MAX_PROJECT_FOLDERS) return false;
  const ids = new Set<string>();
  return value.folders.every(folder => {
    if (!isRecord(folder) || !validId(folder.id) || ids.has(folder.id) ||
      typeof folder.name !== "string" || !cleanProjectName(folder.name) || folder.name.length > MAX_PROJECT_NAME ||
      !Array.isArray(folder.scriptIds) || folder.scriptIds.length > 300 || !folder.scriptIds.every(validId)) return false;
    ids.add(folder.id);
    return true;
  });
}

export function readProjectFolders(storage: Pick<Storage, "getItem">, scriptIds: Iterable<string>): ProjectFoldersState {
  try { return normalizeProjectFolders(JSON.parse(storage.getItem(PROJECT_FOLDERS_STORAGE) || "null"), scriptIds); }
  catch { return { version: 1, folders: [] }; }
}

export function moveProjectScript(state: ProjectFoldersState, scriptId: string, folderId: string | null): ProjectFoldersState {
  if (folderId !== null && !state.folders.some(folder => folder.id === folderId))
    throw new Error("This project folder no longer exists. Choose another project.");
  return {
    version: 1,
    folders: state.folders.map(folder => ({
      ...folder,
      scriptIds: folder.id === folderId
        ? [...folder.scriptIds.filter(id => id !== scriptId), scriptId]
        : folder.scriptIds.filter(id => id !== scriptId),
    })),
  };
}

/** Imported folders never overwrite an existing folder or adopt an unrelated local script. */
export function restoreProjectFolders(
  current: ProjectFoldersState,
  incoming: ProjectFoldersState | undefined,
  scriptIdMap: ReadonlyMap<string, string>,
  allScriptIds: Iterable<string>,
  newId: () => string = () => crypto.randomUUID(),
): ProjectFoldersState {
  const scriptIds = [...allScriptIds];
  const result = normalizeProjectFolders(current, scriptIds);
  if (!incoming) return result;
  if (!validProjectFoldersBackup(incoming)) throw new Error("This backup contains invalid project folders. Your workspace was kept.");
  if (result.folders.length + incoming.folders.length > MAX_PROJECT_FOLDERS)
    throw new Error(`This backup would exceed ${MAX_PROJECT_FOLDERS} project folders. Existing projects were kept.`);
  const usedIds = new Set(result.folders.map(folder => folder.id));
  for (const folder of incoming.folders) {
    let id = newId();
    while (usedIds.has(id)) id = newId();
    usedIds.add(id);
    result.folders.push({
      id,
      name: uniqueRestoredName(cleanProjectName(folder.name), result.folders),
      scriptIds: folder.scriptIds.flatMap(scriptId => {
        const mapped = scriptIdMap.get(scriptId);
        return mapped ? [mapped] : [];
      }),
    });
  }
  return normalizeProjectFolders(result, scriptIds);
}
